<template>
  <div id="app">
    <!-- Header: Hide when showing purchased report -->
    <AppHeader v-if="!hideSiteChrome" />

    <!-- Main Router View -->
    <router-view />

    <!-- Footer: Hide when showing purchased report -->
    <AppFooter v-if="!hideSiteChrome" />

    <!-- Session Timeout Warning Modal -->
    <div v-if="sessionStore.warningShown" class="modal-overlay">
      <div class="modal-content session-warning">
        <button
          type="button"
          class="btn-close-modal"
          @click="sessionStore.hideWarning()"
          aria-label="Close"
        >×</button>
        <h3 class="modal-title">Session Timeout Warning</h3>
        <div class="modal-divider"></div>
        <div class="modal-body">
          <p>Your session is about to timeout in <strong>{{ sessionStore.countdownSeconds }} seconds</strong></p>
        </div>
        <div class="modal-divider"></div>
        <div class="modal-buttons">
          <button @click="sessionStore.keepWorking()" class="btn btn-primary px-4">
            Keep Working
          </button>
          <button @click="sessionStore.handleTimeout()" class="btn btn-outline-secondary px-4">
            Sign Out
          </button>
        </div>
      </div>
    </div>

    <!-- Session Expired Modal -->
    <div v-if="sessionStore.expiredShown" class="modal-overlay">
      <div class="modal-content session-expired">
        <div class="modal-body text-center">
          <img src="/static/images/timeout-door2.png"
               alt="Session Expired"
               class="modal-image mb-4"
               style="max-width: 200px; height: auto;"
               @error="$event.target.style.display='none'">
          <h3 class="modal-title mb-3">You're signed out!</h3>
          <p class="text-muted mb-4">
            You are now signed out of your session.<br>
            The contents of your session may remain<br>
            in your browser memory. Close the<br>
            browser if you are using a public or<br>
            shared computer.
          </p>
        </div>
        <div class="modal-divider"></div>
        <div class="modal-buttons">
          <button @click="returnToSignIn()" class="btn btn-primary px-5">
            Return to Sign In Page
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, onUnmounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useSessionStore } from '@/stores/session'
import { usePaymentStore } from '@/stores/payment'
import AppHeader from '@/components/AppHeader.vue'
import AppFooter from '@/components/AppFooter.vue'

console.log('🔥 APP.VUE SCRIPT LOADED - NEW VERSION 🔥')

const router = useRouter()
const route = useRoute()
const authStore = useAuthStore()
const sessionStore = useSessionStore()
const paymentStore = usePaymentStore()

// Hide header/footer when viewing purchased report or privacy page
const hideSiteChrome = computed(() => {
  return (route.name === 'MyAppeal' && paymentStore.hasPurchased) || route.name === 'Privacy'
})

// Return to sign in page
function returnToSignIn() {
  sessionStore.dismissExpired()
  router.push({ name: 'SignIn' })
}

// Initialize app on mount
onMounted(async () => {
  console.log('[APP] App mounted, validating session...')

  // Validate session if user appears to be signed in
  try {
    await authStore.validateSession()

    console.log('[APP] Session validated, isSignedIn:', authStore.isSignedIn)

    // Initialize session monitoring if signed in
    if (authStore.isSignedIn) {
      console.log('[APP] Initializing session monitoring...')
      sessionStore.initializeSessionMonitoring()
    } else {
      console.log('[APP] User not signed in, skipping session monitoring')
    }
  } catch (error) {
    // Session validation failed or user not signed in
    console.log('[APP] No active session on app init:', error)
  }
})

// Cleanup on unmount
onUnmounted(() => {
  sessionStore.cleanup()
})
</script>

<style>
/* Global styles */
#app {
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

/* Hide elements with v-cloak until Vue is initialized */
[v-cloak] {
  display: none !important;
}

/* Smooth scrolling */
html {
  scroll-behavior: smooth;
}

body {
  margin: 0;
  padding: 0;
}

/* ============================================ */
/* Session Timeout Modal Styles                */
/* ============================================ */

/* Modal Overlay */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
}

/* Modal Content */
.modal-content {
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  position: relative;
  max-width: 90%;
}

.modal-content.session-warning {
  width: 480px;
  padding: 0;
}

.modal-content.session-expired {
  width: 520px;
  padding: 0;
}

/* Close Button */
.btn-close-modal {
  position: absolute;
  top: 15px;
  right: 15px;
  background: none;
  border: none;
  font-size: 28px;
  color: #6c757d;
  cursor: pointer;
  padding: 0;
  width: 30px;
  height: 30px;
  line-height: 1;
  transition: color 0.2s;
}

.btn-close-modal:hover {
  color: #000;
}

/* Modal Title */
.modal-title {
  font-size: 1.25rem;
  font-weight: 600;
  margin: 0;
  padding: 20px 24px;
  color: #333;
}

/* Modal Body */
.modal-body {
  padding: 24px;
  color: #666;
  font-size: 1rem;
}

.modal-body p {
  margin: 0;
}

.modal-body strong {
  color: #333;
}

/* Modal Image */
.modal-image {
  display: block;
  margin: 0 auto;
}

/* Modal Divider */
.modal-divider {
  height: 1px;
  background-color: #dee2e6;
  margin: 0;
}

/* Modal Buttons */
.modal-buttons {
  padding: 20px 24px;
  display: flex;
  gap: 12px;
  justify-content: center;
}

.modal-buttons .btn {
  min-width: 120px;
}
</style>
